import React from 'react';
import { Modal, View, Pressable, StyleSheet, Dimensions } from 'react-native';
import AnimatedMessageContextMenu from '@/components/AnimatedMessageContextMenu';

export interface MessageMenuAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
  isUser: boolean;
}

interface MessageContextMenuOverlayProps {
  visible: boolean;
  anchor: MessageMenuAnchor | null;
  isBookmarked: boolean;
  onShare: () => void;
  onBookmark: () => void;
  onClose: () => void;
}

// Gap between the menu and the anchored message bubble.
const GAP = 8;
// Keep the menu clear of the screen edges.
const EDGE_MARGIN = 8;
// Approximate rendered menu heights (see AnimatedMessageContextMenu styles:
// each menuCard is 52px tall with an 8px column gap). Bill (agent) messages
// show two cards ("Adicionar ao diário" + "Compartilhar"); user messages show
// only "Compartilhar". Used to decide whether the menu fits above the bubble.
const AGENT_MENU_HEIGHT = 52 + 8 + 52;
const USER_MENU_HEIGHT = 52;

/**
 * Renders the message long-press context menu inside a top-level Modal so it
 * always paints above everything else. Anchoring it to the bubble's measured
 * window coordinates lets it sit just above the pressed message, while the
 * Modal's own native window sidesteps the FlatList cell-stacking / clipping
 * issues that kept the inline menu hidden behind neighbouring bubbles.
 */
const MessageContextMenuOverlay = ({
  visible,
  anchor,
  isBookmarked,
  onShare,
  onBookmark,
  onClose,
}: MessageContextMenuOverlayProps) => {
  if (!anchor) return null;

  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

  const horizontalStyle = anchor.isUser
    ? { right: Math.max(EDGE_MARGIN, screenWidth - (anchor.x + anchor.width)) }
    : { left: Math.max(EDGE_MARGIN, anchor.x) };

  // Prefer placing the menu above the bubble (matching the previous
  // "bottom: 100%" placement). If the bubble sits too close to the top of the
  // screen to fit the menu above it, flip it below the bubble so the actions
  // stay on-screen and tappable.
  const menuHeight = anchor.isUser ? USER_MENU_HEIGHT : AGENT_MENU_HEIGHT;
  const fitsAbove = anchor.y - menuHeight - GAP >= EDGE_MARGIN;
  const verticalStyle = fitsAbove
    ? { bottom: screenHeight - anchor.y + GAP }
    : { top: anchor.y + anchor.height + GAP };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.root}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View style={[styles.menuAnchor, horizontalStyle, verticalStyle]} pointerEvents="box-none">
          <AnimatedMessageContextMenu
            visible={visible}
            onShare={onShare}
            onBookmark={onBookmark}
            onClose={onClose}
            position={{ x: 0, y: 0 }}
            isBookmarked={isBookmarked}
            isUserMessage={anchor.isUser}
          />
        </View>
      </View>
    </Modal>
  );
};

export default MessageContextMenuOverlay;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  menuAnchor: {
    position: 'absolute',
  },
});
